import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, Settings, FileDown, Download, Save, Trash2,
  RefreshCw, Loader2, IndianRupee, BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import FoldingCategoryRatesCard from '@/components/wms/FoldingCategoryRatesCard';

const UNIT_COLOURS = {
  bundle: 'bg-amber-100 text-amber-800',
  usn: 'bg-sky-100 text-sky-800',
  action: 'bg-purple-100 text-purple-800',
};

const HRTile = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState({ modules: [], operations: [] });

  useEffect(() => {
    apiClient.get('/hr/operations/catalog').then((r) => setCatalog(r.data))
      .catch(() => toast.error('Failed to load operation catalog'));
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="hr-tile-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/sections/accounts')}
            className="text-[#F9FAFB] hover:bg-white/10"
            data-testid="hr-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/30 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-display">
                HR — Wages & Productivity
              </h1>
              <p className="text-sm text-[#E8DCC8]">
                {catalog.operations.length} billable operations across{' '}
                {catalog.modules.length} modules
              </p>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs defaultValue="dashboard">
          <TabsList data-testid="hr-tabs">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4 mr-1" /> Operations Dashboard
            </TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-rates">
              <Settings className="w-4 h-4 mr-1" /> Piece-Rate Master
            </TabsTrigger>
            <TabsTrigger value="wages" data-testid="tab-wages">
              <IndianRupee className="w-4 h-4 mr-1" /> Wages Report
            </TabsTrigger>
            <TabsTrigger value="folding-cat" data-testid="tab-folding-cat" onClick={() => navigate('/hr/folding-category-wages')}>
              <IndianRupee className="w-4 h-4 mr-1" /> Folding × Category 2
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">
              <Users className="w-4 h-4 mr-1" /> Activity Drill-Down
            </TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard">
            <DashboardTab catalog={catalog} />
          </TabsContent>
          <TabsContent value="rates">
            <PieceRatesTab catalog={catalog} />
          </TabsContent>
          <TabsContent value="wages">
            <WagesTab />
          </TabsContent>
          <TabsContent value="activity">
            <ActivityDrilldownTab catalog={catalog} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

/* ----------------------------- DASHBOARD ----------------------------- */
const DashboardTab = ({ catalog }) => {
  const today = new Date().toISOString().slice(0, 10);
  const monthBack = new Date(Date.now() - 30 * 86400000)
    .toISOString().slice(0, 10);
  const [module, setModule] = useState('all');
  const [operation, setOperation] = useState('all');
  const [staffId, setStaffId] = useState('');
  const [dateFrom, setDateFrom] = useState(monthBack);
  const [dateTo, setDateTo] = useState(today);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], totals: null });

  const operationsForModule = useMemo(
    () => (module === 'all'
      ? catalog.operations
      : catalog.operations.filter((o) => o.module === module)),
    [module, catalog.operations],
  );

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (module !== 'all') params.module = module;
      if (operation !== 'all') params.operation_code = operation;
      if (staffId.trim()) params.staff_id = staffId.trim();
      const r = await apiClient.get('/hr/dashboard', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dashboard load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line

  return (
    <Card className="mt-4" data-testid="hr-dashboard-card">
      <CardHeader>
        <CardTitle className="text-base">Quantities by Staff × Operation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label>Module</Label>
            <Select value={module} onValueChange={(v) => {
              setModule(v); setOperation('all');
            }}>
              <SelectTrigger data-testid="dash-module-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {catalog.modules.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Operation</Label>
            <Select value={operation} onValueChange={setOperation}>
              <SelectTrigger data-testid="dash-operation-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All operations</SelectItem>
                {operationsForModule.map((o) => (
                  <SelectItem key={o.operation_code} value={o.operation_code}>
                    {o.operation}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Staff ID</Label>
            <Input
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              placeholder="e.g. V0121"
              data-testid="dash-staff-input"
            />
          </div>
          <div>
            <Label>From</Label>
            <Input
              type="date" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="dash-from-input"
            />
          </div>
          <div>
            <Label>To</Label>
            <Input
              type="date" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="dash-to-input"
            />
          </div>
        </div>
        <div className="flex justify-between items-center">
          <Button
            onClick={load} disabled={loading}
            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
            data-testid="dash-apply-btn"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
              : <RefreshCw className="w-4 h-4 mr-2" />}
            Apply Filters
          </Button>
          {data.totals && (
            <div className="flex gap-2 text-sm">
              <Badge variant="outline" data-testid="dash-total-bundles">
                Bundles: {data.totals.bundle_qty?.toLocaleString('en-IN') || 0}
              </Badge>
              <Badge variant="outline" data-testid="dash-total-usns">
                USNs: {data.totals.usn_qty?.toLocaleString('en-IN') || 0}
              </Badge>
              <Badge variant="outline" data-testid="dash-total-events">
                Events: {data.totals.event_count?.toLocaleString('en-IN') || 0}
              </Badge>
            </div>
          )}
        </div>
        <div className="overflow-auto max-h-[60vh]">
          <Table>
            <TableHeader className="bg-[#1A1F2E] text-white sticky top-0">
              <TableRow>
                <TableHead className="text-white">Module</TableHead>
                <TableHead className="text-white">Operation</TableHead>
                <TableHead className="text-white">Staff ID</TableHead>
                <TableHead className="text-white">Staff Name</TableHead>
                <TableHead className="text-right text-white">Bundles</TableHead>
                <TableHead className="text-right text-white">USNs</TableHead>
                <TableHead className="text-right text-white">Events</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data.rows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-400 py-8">
                    No data for this filter
                  </TableCell>
                </TableRow>
              )}
              {data.rows.map((r, i) => (
                <TableRow key={i} data-testid={`dash-row-${r.staff_id}-${r.operation_code}`}>
                  <TableCell className="text-xs">{r.module}</TableCell>
                  <TableCell className="text-xs">{r.operation}</TableCell>
                  <TableCell className="font-mono text-xs">{r.staff_id}</TableCell>
                  <TableCell className="text-xs">{r.staff_name || '—'}</TableCell>
                  <TableCell className="text-right">
                    {r.bundle_qty?.toLocaleString('en-IN') || 0}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.usn_qty?.toLocaleString('en-IN') || 0}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.event_count?.toLocaleString('en-IN') || 0}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ----------------------------- PIECE RATES ----------------------------- */
const PieceRatesTab = ({ catalog }) => {
  const [rates, setRates] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [draft, setDraft] = useState({
    operation_code: '', unit_type: 'bundle', rate: '', remarks: '',
  });

  const load = async () => {
    const r = await apiClient.get('/hr/piece-rates');
    setRates(r.data.rates || []);
    setIsAdmin(r.data.is_admin);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const selectedOp = useMemo(
    () => catalog.operations.find((o) => o.operation_code === draft.operation_code),
    [draft.operation_code, catalog.operations],
  );
  const validUnits = selectedOp
    ? [
        ...(selectedOp.has_bundle_unit ? ['bundle'] : []),
        ...(selectedOp.has_usn_unit ? ['usn'] : []),
        ...(selectedOp.has_action_unit ? ['action'] : []),
      ] : ['bundle', 'usn', 'action'];

  const save = async () => {
    if (!draft.operation_code || !draft.rate) {
      toast.error('Pick operation and enter rate');
      return;
    }
    try {
      await apiClient.post('/hr/piece-rates', {
        operation_code: draft.operation_code,
        unit_type: draft.unit_type,
        rate: parseFloat(draft.rate),
        remarks: draft.remarks || null,
      });
      toast.success('Piece rate saved');
      setDraft({ operation_code: '', unit_type: 'bundle', rate: '', remarks: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const del = async (code, unit) => {
    if (!window.confirm(`Deactivate rate for ${code} / ${unit}?`)) return;
    try {
      await apiClient.delete(`/hr/piece-rates/${code}/${unit}`);
      toast.success('Rate deactivated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <>
    <Card className="mt-4" data-testid="hr-rates-card">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-base">Piece-Rate Master</CardTitle>
          <Badge variant={isAdmin ? 'default' : 'outline'}
                 data-testid="rates-admin-badge">
            {isAdmin ? 'Admin — Edit Mode' : 'View Only'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdmin && (
          <div className="border rounded-md p-3 bg-emerald-50/30 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2">
              <Label>Operation</Label>
              <Select
                value={draft.operation_code}
                onValueChange={(v) => setDraft({ ...draft, operation_code: v })}
              >
                <SelectTrigger data-testid="rate-op-select">
                  <SelectValue placeholder="Select operation…" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {catalog.operations.map((o) => (
                    <SelectItem
                      key={o.operation_code} value={o.operation_code}
                    >
                      {o.module} · {o.operation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unit</Label>
              <Select
                value={draft.unit_type}
                onValueChange={(v) => setDraft({ ...draft, unit_type: v })}
              >
                <SelectTrigger data-testid="rate-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {validUnits.map((u) => (
                    <SelectItem key={u} value={u}>per {u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rate (₹)</Label>
              <Input
                type="number" step="0.01"
                value={draft.rate}
                onChange={(e) => setDraft({ ...draft, rate: e.target.value })}
                data-testid="rate-rate-input"
              />
            </div>
            <Button
              onClick={save}
              disabled={!draft.operation_code || !draft.rate}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]"
              data-testid="rate-save-btn"
            >
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Module</TableHead>
              <TableHead>Operation</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Rate (₹)</TableHead>
              <TableHead>Effective from</TableHead>
              <TableHead>Updated by</TableHead>
              {isAdmin && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rates.length && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 7 : 6}
                           className="text-center text-gray-400 py-6">
                  No piece rates set yet
                </TableCell>
              </TableRow>
            )}
            {rates.map((r) => (
              <TableRow key={`${r.operation_code}-${r.unit_type}`}
                        data-testid={`rate-row-${r.operation_code}-${r.unit_type}`}>
                <TableCell className="text-xs">{r.module || '—'}</TableCell>
                <TableCell className="text-xs">{r.operation || r.operation_code}</TableCell>
                <TableCell>
                  <Badge className={UNIT_COLOURS[r.unit_type] || ''}
                         variant="outline">
                    per {r.unit_type}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono">
                  ₹{r.rate?.toFixed(2)}
                </TableCell>
                <TableCell className="text-xs">{r.effective_from}</TableCell>
                <TableCell className="text-xs">{r.updated_by || '—'}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => del(r.operation_code, r.unit_type)}
                      data-testid={`rate-delete-${r.operation_code}-${r.unit_type}`}
                    >
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
    <FoldingCategoryRatesCard />
    </>
  );
};

/* ----------------------------- WAGES REPORT ----------------------------- */
const WagesTab = () => {
  const today = new Date().toISOString().slice(0, 10);
  const monthBack = new Date(Date.now() - 30 * 86400000)
    .toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthBack);
  const [dateTo, setDateTo] = useState(today);
  const [staffId, setStaffId] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ staff_totals: [], rows: [], grand_total: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (staffId.trim()) params.staff_id = staffId.trim();
      const r = await apiClient.get('/hr/wages-report', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report failed');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const exportFile = (kind) => {
    const params = new URLSearchParams({
      date_from: dateFrom, date_to: dateTo,
    });
    if (staffId.trim()) params.append('staff_id', staffId.trim());
    const url = `${process.env.REACT_APP_BACKEND_URL}`
      + `/api/hr/wages-report/export.${kind}?${params.toString()}`;
    const token = localStorage.getItem('session_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `wages_${dateFrom}_to_${dateTo}.${kind}`;
        a.click();
      })
      .catch((e) => toast.error(`Export failed: ${e.message}`));
  };

  return (
    <Card className="mt-4" data-testid="hr-wages-card">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-base">Wages Report</CardTitle>
          <div className="text-lg font-display font-bold text-[#2D5F4F]"
               data-testid="wages-grand-total">
            ₹ {data.grand_total?.toLocaleString('en-IN',
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label>From</Label>
            <Input
              type="date" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="wages-from-input"
            />
          </div>
          <div>
            <Label>To</Label>
            <Input
              type="date" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="wages-to-input"
            />
          </div>
          <div>
            <Label>Staff ID (optional)</Label>
            <Input
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              placeholder="e.g. V0121"
              data-testid="wages-staff-input"
            />
          </div>
          <Button
            onClick={load} disabled={loading}
            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
            data-testid="wages-apply-btn"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
              : <RefreshCw className="w-4 h-4 mr-2" />}
            Compute
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline" onClick={() => exportFile('csv')}
              disabled={!data.rows.length}
              data-testid="wages-csv-btn"
            >
              <FileDown className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button
              variant="outline" onClick={() => exportFile('pdf')}
              disabled={!data.rows.length}
              data-testid="wages-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        </div>

        <div className="overflow-auto max-h-[60vh]">
          <Table>
            <TableHeader className="bg-[#1A1F2E] text-white sticky top-0">
              <TableRow>
                <TableHead className="text-white">Staff ID</TableHead>
                <TableHead className="text-white">Staff Name</TableHead>
                <TableHead className="text-white">Module</TableHead>
                <TableHead className="text-white">Operation</TableHead>
                <TableHead className="text-right text-white">Bundles</TableHead>
                <TableHead className="text-right text-white">USNs</TableHead>
                <TableHead className="text-white">Units Paid</TableHead>
                <TableHead className="text-right text-white">Wages ₹</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data.rows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-400 py-8">
                    No wages — check piece rates are set for the operations
                  </TableCell>
                </TableRow>
              )}
              {data.staff_totals?.map((s) => (
                <React.Fragment key={s.staff_id}>
                  {s.lines.map((r, i) => (
                    <TableRow key={`${s.staff_id}-${i}`}
                              data-testid={`wages-row-${s.staff_id}-${r.operation_code}`}>
                      <TableCell className="font-mono text-xs">
                        {i === 0 ? s.staff_id : ''}
                      </TableCell>
                      <TableCell className="text-xs">
                        {i === 0 ? s.staff_name : ''}
                      </TableCell>
                      <TableCell className="text-xs">{r.module}</TableCell>
                      <TableCell className="text-xs">{r.operation}</TableCell>
                      <TableCell className="text-right">
                        {r.bundle_qty?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.usn_qty?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.breakdown.map((p, ix) => (
                          <Badge key={ix}
                                 className={`${UNIT_COLOURS[p.unit]} mr-1`}
                                 variant="outline">
                            {p.qty} × ₹{p.rate}
                          </Badge>
                        ))}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ₹{r.wages?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-emerald-50/60 font-semibold"
                            data-testid={`wages-total-${s.staff_id}`}>
                    <TableCell colSpan={7} className="text-right">
                      {s.staff_name} total
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ₹{s.total_wages?.toLocaleString('en-IN',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ----------------------- ACTIVITY DRILL-DOWN TAB ----------------------- */
const ActivityDrilldownTab = ({ catalog }) => {
  const monthAgo = new Date();
  monthAgo.setDate(monthAgo.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [dateFrom, setDateFrom] = useState(fmt(monthAgo));
  const [dateTo, setDateTo] = useState(fmt(new Date()));
  const [moduleFilter, setModuleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [grain, setGrain] = useState('raw');
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const moduleOptions = (catalog?.modules || []);

  const fetchStaffList = async () => {
    setStaffLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (moduleFilter !== 'all') params.append('module', moduleFilter);
      if (search) params.append('search', search);
      const r = await apiClient.get(`/hr/staff-activity?${params.toString()}`);
      setStaffList(r.data.staff || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load staff list');
    } finally {
      setStaffLoading(false);
    }
  };

  const fetchDrilldown = async (staff_id) => {
    setDrillLoading(true);
    try {
      const params = new URLSearchParams({ grain });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (moduleFilter !== 'all') params.append('module', moduleFilter);
      const r = await apiClient.get(
        `/hr/staff-activity/${encodeURIComponent(staff_id)}?${params.toString()}`,
      );
      setDrillData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load drill-down');
    } finally {
      setDrillLoading(false);
    }
  };

  useEffect(() => { fetchStaffList(); }, []); // eslint-disable-line
  useEffect(() => { if (selectedStaff) fetchDrilldown(selectedStaff); }, [selectedStaff, grain]); // eslint-disable-line

  const handleExportCsv = () => {
    if (!selectedStaff) return;
    const params = new URLSearchParams({ grain });
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (moduleFilter !== 'all') params.append('module', moduleFilter);
    const url = `${(process.env.REACT_APP_BACKEND_URL || '')}/api/hr/staff-activity/${encodeURIComponent(selectedStaff)}/export.csv?${params.toString()}`;
    const tok = localStorage.getItem('session_token');
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `hr_activity_${selectedStaff}_${grain}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success(`Exported ${grain === 'raw' ? 'raw activity' : 'daily roll-up'}`);
      })
      .catch(() => toast.error('Export failed'));
  };

  const inr = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4">
      {/* Filter strip */}
      <Card className="md:col-span-12">
        <CardContent className="p-3 grid grid-cols-2 md:grid-cols-6 gap-2">
          <div>
            <Label className="text-xs">From Date</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="hr-act-date-from" />
          </div>
          <div>
            <Label className="text-xs">To Date</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="hr-act-date-to" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Module</Label>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger data-testid="hr-act-module-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {moduleOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Search Staff</Label>
            <Input
              placeholder="Name or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchStaffList()}
              data-testid="hr-act-search"
            />
          </div>
          <div className="md:col-span-6 flex justify-end gap-2">
            <Button onClick={fetchStaffList} className="bg-indigo-600 hover:bg-indigo-700" data-testid="hr-act-apply">
              {staffLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Left pane: staff list */}
      <Card className="md:col-span-4 max-h-[600px] overflow-auto">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Staff <Badge className="bg-stone-100 text-stone-700 ml-2 border-0">{staffList.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 px-2">
          {staffLoading ? (
            <div className="text-center text-sm text-stone-500 py-6">Loading…</div>
          ) : staffList.length === 0 ? (
            <div className="text-center text-sm text-stone-500 py-6">No activity in this window.</div>
          ) : staffList.map((s) => {
            const sel = selectedStaff === s.staff_id;
            return (
              <button
                key={s.staff_id}
                onClick={() => setSelectedStaff(s.staff_id)}
                className={`w-full text-left rounded-md px-3 py-2 transition ${sel ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-stone-50 border border-transparent'}`}
                data-testid={`hr-act-staff-${s.staff_id}`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm truncate">{s.staff_name}</span>
                  <span className="text-xs font-mono text-stone-500">{s.staff_id}</span>
                </div>
                <div className="flex justify-between items-center mt-1 text-xs">
                  <span className="text-stone-600">{s.total_actions} actions · {s.total_usns} USNs</span>
                  <span className="font-mono text-emerald-700">{inr(s.total_wage)}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {s.modules_touched.slice(0, 4).map((m) => (
                    <Badge key={m} className="bg-stone-100 text-stone-700 border-0 text-[9px]">{m}</Badge>
                  ))}
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Right pane: drill-down */}
      <Card className="md:col-span-8">
        {!selectedStaff ? (
          <CardContent className="p-12 text-center text-stone-500" data-testid="hr-act-empty-pane">
            <Users className="h-10 w-10 mx-auto mb-2 text-stone-300" />
            Select a staff from the left to view their activity drill-down.
          </CardContent>
        ) : (
          <>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <CardTitle className="text-base">{drillData?.staff?.staff_name || selectedStaff}</CardTitle>
                  <p className="text-xs font-mono text-stone-500">{selectedStaff}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={grain} onValueChange={setGrain}>
                    <SelectTrigger className="h-8 w-40" data-testid="hr-act-grain-toggle">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="raw">Raw activity stream</SelectItem>
                      <SelectItem value="daily">Daily roll-up</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleExportCsv} data-testid="hr-act-export-btn">
                    <Download className="w-4 h-4 mr-1" /> Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {drillLoading ? (
                <div className="p-6 text-center text-stone-500"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
              ) : drillData ? (
                <>
                  {/* KPIs */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {[
                      { label: 'Actions', value: drillData.summary.total_actions, color: 'text-indigo-700' },
                      { label: 'Bundles', value: drillData.summary.total_bundles, color: 'text-amber-700' },
                      { label: 'USNs', value: drillData.summary.total_usns, color: 'text-sky-700' },
                      { label: 'Wage (INR)', value: inr(drillData.summary.total_wage), color: 'text-emerald-700' },
                    ].map((k) => (
                      <div key={k.label} className="bg-stone-50 rounded p-2 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-stone-500">{k.label}</div>
                        <div className={`text-lg font-semibold ${k.color}`} data-testid={`hr-act-kpi-${k.label.toLowerCase().replace(/[^a-z]/g, '')}`}>{k.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Module pills */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {Object.entries(drillData.summary.by_module || {}).map(([m, n]) => (
                      <Badge key={m} className="bg-indigo-50 text-indigo-700 border-0 text-xs">{m}: {n}</Badge>
                    ))}
                  </div>

                  {/* Table */}
                  <div className="border rounded-md max-h-[480px] overflow-auto">
                    {grain === 'raw' ? (
                      <Table data-testid="hr-act-table-raw">
                        <TableHeader className="sticky top-0 bg-white z-10">
                          <TableRow>
                            <TableHead>Timestamp (IST)</TableHead>
                            <TableHead>Module</TableHead>
                            <TableHead>Operation</TableHead>
                            <TableHead>Doc Ref</TableHead>
                            <TableHead className="text-right">Bundles</TableHead>
                            <TableHead className="text-right">USNs</TableHead>
                            <TableHead className="text-right">Wage</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {drillData.rows.map((r, i) => (
                            <TableRow key={`${r.timestamp}-${i}`}>
                              <TableCell className="text-xs whitespace-nowrap">{r.timestamp_ist}</TableCell>
                              <TableCell className="text-xs">{r.module}</TableCell>
                              <TableCell className="text-xs">{r.operation}</TableCell>
                              <TableCell className="text-xs font-mono">{r.doc_ref}</TableCell>
                              <TableCell className="text-right text-xs">{r.bundles || '—'}</TableCell>
                              <TableCell className="text-right text-xs">{r.usns || '—'}</TableCell>
                              <TableCell className="text-right text-xs font-mono text-emerald-700">{inr(r.wage)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Table data-testid="hr-act-table-daily">
                        <TableHeader className="sticky top-0 bg-white z-10">
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Module</TableHead>
                            <TableHead>Operation</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                            <TableHead className="text-right">Bundles</TableHead>
                            <TableHead className="text-right">USNs</TableHead>
                            <TableHead className="text-right">Wage</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {drillData.rows.map((r, i) => (
                            <TableRow key={`${r.date}-${r.operation}-${i}`}>
                              <TableCell className="text-xs whitespace-nowrap">{r.date}</TableCell>
                              <TableCell className="text-xs">{r.module}</TableCell>
                              <TableCell className="text-xs">{r.operation}</TableCell>
                              <TableCell className="text-right text-xs">{r.actions}</TableCell>
                              <TableCell className="text-right text-xs">{r.bundles || '—'}</TableCell>
                              <TableCell className="text-right text-xs">{r.usns || '—'}</TableCell>
                              <TableCell className="text-right text-xs font-mono text-emerald-700">{inr(r.wage)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </>
              ) : null}
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};


export default HRTile;
